/**
 * Mutateurs de run NG+ — 1 actif par run, choisi à l'ascension.
 */

export type AscensionRunMutatorId =
  | 'none'
  | 'mono_type'
  | 'gold_rush'
  | 'prodigy'
  | 'glass_world'
  | 'marathon'

export type AscensionRunMutatorEffect = {
  dpsMult: number
  goldMult: number
  shinyMult: number
  xpMult: number
  enemyHpMult: number
  rewardMult: number
  /**
   * Bonus de points d'ascension en fin de run, en % des points de BASE (additif,
   * hors multiplicateurs contrat/catch-up/capstone — évite les montants aberrants).
   */
  basePointsBonusPct: number
  /** Si true, pas de burst or post-ascension. */
  skipAwakeningBurst: boolean
}

export type AscensionRunMutatorDef = {
  id: Exclude<AscensionRunMutatorId, 'none'>
  labelFr: string
  labelEn: string
  descFr: string
  descEn: string
  effect: AscensionRunMutatorEffect
}

const NO_MUTATOR: AscensionRunMutatorEffect = {
  dpsMult: 1,
  goldMult: 1,
  shinyMult: 1,
  xpMult: 1,
  enemyHpMult: 1,
  rewardMult: 1,
  basePointsBonusPct: 0,
  skipAwakeningBurst: false,
}

export const ASCENSION_RUN_MUTATORS: readonly AscensionRunMutatorDef[] = [
  {
    id: 'mono_type',
    labelFr: 'Mono-type',
    labelEn: 'Mono-type',
    descFr: '+25 % DPS si équipe mono-type, mais −15 % or',
    descEn: '+25% DPS if mono-type team, but −15% gold',
    effect: { ...NO_MUTATOR, dpsMult: 1.25, goldMult: 0.85 },
  },
  {
    id: 'gold_rush',
    labelFr: 'Ruée d’or',
    labelEn: 'Gold rush',
    descFr: '×2 or, mais +20 % PV ennemis',
    descEn: '×2 gold, but +20% enemy HP',
    effect: { ...NO_MUTATOR, goldMult: 2, enemyHpMult: 1.2 },
  },
  {
    id: 'prodigy',
    labelFr: 'Prodige',
    labelEn: 'Prodigy',
    descFr: '+30 % XP, mais −15 % or',
    descEn: '+30% XP, but −15% gold',
    effect: { ...NO_MUTATOR, xpMult: 1.3, goldMult: 0.85 },
  },
  {
    id: 'glass_world',
    labelFr: 'Monde fragile',
    labelEn: 'Glass world',
    descFr: '−25 % PV ennemis, mais −20 % récompenses',
    descEn: '−25% enemy HP, but −20% rewards',
    effect: { ...NO_MUTATOR, enemyHpMult: 0.75, rewardMult: 0.8 },
  },
  {
    id: 'marathon',
    labelFr: 'Marathon',
    labelEn: 'Marathon',
    descFr: '+15 % points d’ascension (sur les points de base), sans burst or post-ascension',
    descEn: '+15% ascension points (on base points), no post-ascension gold burst',
    effect: { ...NO_MUTATOR, basePointsBonusPct: 0.15, skipAwakeningBurst: true },
  },
] as const

export const ASCENSION_RUN_MUTATOR_IDS = ASCENSION_RUN_MUTATORS.map((m) => m.id)

export function normalizeAscensionRunMutatorId(raw: unknown): AscensionRunMutatorId {
  const id = String(raw ?? 'none').trim() as AscensionRunMutatorId
  if (id === 'none') return 'none'
  return ASCENSION_RUN_MUTATOR_IDS.includes(id as Exclude<AscensionRunMutatorId, 'none'>)
    ? id
    : 'none'
}

export function getAscensionRunMutatorDef(
  id: AscensionRunMutatorId,
): AscensionRunMutatorDef | null {
  if (id === 'none') return null
  return ASCENSION_RUN_MUTATORS.find((m) => m.id === id) ?? null
}

export function getAscensionRunMutatorEffect(
  id: AscensionRunMutatorId,
  opts?: { monoTypeTeam?: boolean },
): AscensionRunMutatorEffect {
  const def = getAscensionRunMutatorDef(id)
  if (!def) return { ...NO_MUTATOR }
  const eff = { ...def.effect }
  if (id === 'mono_type' && !opts?.monoTypeTeam) {
    eff.dpsMult = 1
  }
  return eff
}
